/**
 * Where the nodes of a conversation sit, and how they are laid out when nobody
 * has said.
 *
 * A conversation is a graph and is read by looking at it: which answer leads
 * where, what rejoins what, where it ends. That only works if the nodes hold
 * still, so where each one sits is written down with it. A conversation written
 * before there was a canvas has no positions at all, so one is worked out for
 * it: rank by how far along it is, stack within the rank, which is the shape a
 * conversation actually has.
 */

export interface Placed {
  id: string;
  x: number;
  y: number;
}

export type NodeKind =
  'line' | 'branch' | 'directory' | 'start' | 'priority' | 'note' | 'checkpoint';

export interface Node {
  id: string;
  kind?: NodeKind;
  /** What has to hold, for the checks that can tell a broken one. */
  when?: readonly { item?: number; wearing?: number; quest?: string; deedOf?: number }[];
  pass?: string;
  fail?: string;
  /** The one way on, for anything that has exactly one. */
  next?: string;
  /** What it says, for the ones that are read on the canvas itself. */
  text?: string;
  /** Said once and never again, which is worth a mark on the node. */
  once?: boolean;
  /** A checkpoint that ends the talk the first time it is reached. */
  stops?: boolean;
  at?: { x: number; y: number };
  /**
   * Whether reaching this ends the conversation.
   *
   * Opening a window is the last thing an answer does: there is nothing left to
   * say once it is up, so nothing that ends the talk is drawn with a way out
   * for a wire that could never be followed.
   */
  ends?: boolean;
  options: readonly { text: string; goto: string; ends?: boolean }[];
}

/** How far apart the ranks are, and the rows within one. */
export const COLUMN = 260;
export const ROW = 130;

/**
 * Everywhere one node leads, and what each way out is called.
 *
 * Every node has at least one, because a node with no way out is a node the
 * conversation walks into and cannot leave. A line nobody has written an answer
 * for leads on with a Continue, which is what the player is shown as well.
 */
export function waysOut(node: Node): { label: string; goto: string }[] {
  if (node.kind === 'note') return [];
  if (node.ends === true && node.options.length === 0) return [];

  if (node.kind === 'branch') {
    return [
      { label: 'True', goto: node.pass ?? '' },
      { label: 'False', goto: node.fail ?? '' },
    ];
  }

  if (node.kind === 'start' || node.kind === 'priority') {
    return [{ label: node.kind === 'start' ? 'opens at' : 'instead', goto: node.next ?? '' }];
  }

  // A checkpoint says nothing and leads one way: on the first time through, or
  // straight away on the way back if it ended the talk when it was reached.
  if (node.kind === 'checkpoint') {
    return [{ label: node.stops ? 'next time' : 'carries on', goto: node.next ?? '' }];
  }

  if (node.options.length > 0) {
    // An answer that ends the talk has nowhere to go, so it is drawn without a
    // pin rather than with one nothing may be hung on.
    const ways = node.options
      .filter((option) => option.ends !== true)
      .map((option) => ({ label: option.text, goto: option.goto }));

    // A greeting said once still needs somewhere to send whoever has already
    // heard it, and its answers are no use for that: they belong to the
    // greeting. So it gets one more way out, and only when it needs one.
    if (node.once) ways.push({ label: 'afterwards', goto: node.next ?? '' });

    return ways;
  }

  // A directory with nothing in it is being written; a line with nothing in it
  // carries on. A line that opens a window has nothing after it at all.
  if (node.ends === true) return [];

  return node.kind === 'directory' ? [] : [{ label: 'Continue', goto: node.next ?? '' }];
}

/** Whether anything may lead into this one. A start is where things begin. */
export function takesInput(node: Node): boolean {
  return node.kind !== 'start' && node.kind !== 'priority' && node.kind !== 'note';
}

/**
 * A place for every node, from the ones that have one and a layout for the rest.
 *
 * Ranked by the fewest steps from a way in, so answers march left to right and
 * anything two answers both lead to lands to the right of both. Nodes that
 * already carry a position keep it, so a layout never moves what somebody has
 * arranged by hand.
 */
export function placeAll(nodes: readonly Node[]): Map<string, Placed> {
  const byId = new Map(nodes.map((node) => [node.id, node]));

  const pointedAt = new Set<string>();
  for (const node of nodes) {
    for (const way of waysOut(node)) {
      if (way.goto.length > 0 && way.goto !== node.id) pointedAt.add(way.goto);
    }
  }

  // How far along each node is: the most answers from a way in rather than the
  // fewest. A line two answers lead into, one of them straight from the top and
  // one from further along, belongs to the right of both of them; ranking it by
  // the short way round draws half its wires backwards.
  const rank = new Map<string, number>();
  for (const node of nodes) {
    if (!pointedAt.has(node.id)) rank.set(node.id, 0);
  }

  // Nothing unpointed at all means the whole thing is a loop, and it has to
  // start somewhere: the first line, which is what the server would say first.
  if (rank.size === 0 && nodes[0]) rank.set(nodes[0].id, 0);

  // Relaxed until it settles, and no more times than there are nodes: a loop
  // would otherwise push its own rank up forever.
  for (let pass = 0; pass < nodes.length + 1; pass++) {
    let moved = false;

    for (const node of nodes) {
      const depth = rank.get(node.id);
      if (depth === undefined) continue;

      for (const way of waysOut(node)) {
        if (way.goto.length === 0 || !byId.has(way.goto)) continue;

        const wanted = depth + 1;
        if ((rank.get(way.goto) ?? -1) >= wanted) continue;

        rank.set(way.goto, wanted);
        moved = true;
      }
    }

    if (!moved) break;
  }

  // Anything the walk never reached, off to the side rather than on top of the
  // first column.
  let stranded = 0;
  for (const node of nodes) {
    if (rank.has(node.id)) continue;
    rank.set(node.id, 0);
    stranded += 1;
  }

  const placed = new Map<string, Placed>();
  const taken = new Map<number, number>();

  for (const node of nodes) {
    if (node.at) {
      placed.set(node.id, { id: node.id, x: node.at.x, y: node.at.y });
      continue;
    }

    const column = rank.get(node.id) ?? 0;
    const row = taken.get(column) ?? 0;
    taken.set(column, row + 1);

    placed.set(node.id, { id: node.id, x: column * COLUMN, y: row * ROW });
  }

  void stranded;
  return placed;
}

/** The bounds of everything placed, for fitting a whole conversation on screen. */
export function boundsOf(placed: Iterable<Placed>): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const one of placed) {
    minX = Math.min(minX, one.x);
    minY = Math.min(minY, one.y);
    maxX = Math.max(maxX, one.x);
    maxY = Math.max(maxY, one.y);
  }

  if (minX === Infinity) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { minX, minY, maxX, maxY };
}

/**
 * What is wrong with a conversation, in the words somebody would use about it.
 *
 * Only what is actually wrong. A line nothing points at is ordinary, because
 * any line can be reached by a priority or by being what the start opens at;
 * what is not ordinary is a wire pointing at something that is not there, two
 * nodes sharing a name, or a way out of a node that goes nowhere at all.
 */
export function problemsWith(nodes: readonly Node[]): string[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const said: string[] = [];

  const starts = nodes.filter((node) => node.kind === 'start');
  if (starts.length === 0) said.push('There is no Start, so nothing opens the conversation.');
  if (starts.length > 1) said.push('There is more than one Start.');

  const seen = new Set<string>();
  for (const node of nodes) {
    if (seen.has(node.id)) said.push(`There are two nodes called ${node.id}.`);
    seen.add(node.id);

    if (node.kind === 'note') continue;

    const ways = waysOut(node);
    for (const way of ways) {
      if (way.goto.length === 0) continue;

      // Round in a circle of one. Whatever wrote it, it is not a conversation:
      // whoever reaches it is handed the same line again forever.
      if (way.goto === node.id) {
        said.push(`${node.id} leads back to itself.`);
        continue;
      }

      if (byId.has(way.goto)) continue;

      said.push(`${node.id} points at ${way.goto}, which is not there.`);
    }

    if (node.kind === 'start' && ways.every((way) => way.goto.length === 0)) {
      said.push('The Start does not lead anywhere yet.');
    }

    if (node.kind === 'branch' && ways.every((way) => way.goto.length === 0)) {
      said.push(`${node.id} is a branch with nowhere to go either way.`);
    }

    if (node.kind === 'priority' && ways.every((way) => way.goto.length === 0)) {
      said.push(`${node.id} takes over the conversation and then leads nowhere.`);
    }

    // A checkpoint is a place to come back to, so there has to be something on
    // the other side of it worth coming back for.
    if (node.kind === 'checkpoint' && (node.next ?? '').length === 0) {
      said.push(`${node.id} is a place to come back to that leads nowhere.`);
    }

    // A condition that names nothing can never be true, so whatever hangs off
    // it never happens. Silent until now, which is how a branch came to send
    // everybody down its False side forever.
    for (const held of node.when ?? []) {
      if (held.item === 0 || held.wearing === 0 || held.deedOf === 0 || held.quest === '') {
        said.push(`${node.id} asks about something that has not been chosen.`);
        break;
      }
    }

    // Said once, and then nothing. The second visit gets silence, which reads
    // as a broken character rather than as a greeting somebody has heard.
    if (node.once && (node.next ?? '').length === 0) {
      said.push(`${node.id} is said only once and has nothing to go on to afterwards.`);
    }
  }

  return said;
}

/** Whether a node has anything wrong with it, for a mark on the node itself. */
export function troubled(node: Node, nodes: readonly Node[]): boolean {
  const known = new Set(nodes.map((one) => one.id));
  if (node.kind === 'note') return false;

  const nothingChosen = (node.when ?? []).some(
    (held) => held.item === 0 || held.wearing === 0 || held.deedOf === 0 || held.quest === '',
  );
  if (nothingChosen) return true;

  const ways = waysOut(node);
  if (ways.some((way) => way.goto === node.id)) return true;
  if (ways.some((way) => way.goto.length > 0 && !known.has(way.goto))) return true;
  if (node.once && (node.next ?? '').length === 0) return true;

  return (
    (node.kind === 'start' ||
      node.kind === 'branch' ||
      node.kind === 'priority' ||
      node.kind === 'checkpoint') &&
    ways.every((way) => way.goto.length === 0)
  );
}
